import React, { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Spinner, Table } from "flowbite-react";
import {
  HiBadgeCheck,
  HiCalendar,
  HiCheck,
  HiClipboardCopy,
  HiIdentification,
  HiLocationMarker,
  HiMail,
  HiPhone,
  HiSearch,
  HiUser,
} from "react-icons/hi";
import { QUERIES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";
import { formatDocument } from "../utils/utils";

const LocalizaPessoaPage = () => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [erro, setErro] = useState("");
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [copiedCpf, setCopiedCpf] = useState("");
  const copyTimeoutRef = useRef(null);
  const totalResultados = useMemo(() => resultados.length, [resultados.length]);

  const handleCopyCpf = async (cpf) => {
    if (!cpf) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(cpf);
      } else {
        const temp = document.createElement("textarea");
        temp.value = cpf;
        temp.setAttribute("readonly", "");
        temp.style.position = "absolute";
        temp.style.left = "-9999px";
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
      }
      setCopiedCpf(cpf);
      window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => setCopiedCpf(""), 2000);
    } catch (err) {
      console.error("Falha ao copiar CPF", err);
    }
  };

  const onSubmit = async (data) => {
    const algumPreenchido = Object.values(data).some(
      (val) => val && val.trim() !== ""
    );
    if (!algumPreenchido) {
      setError("form", {
        type: "manual",
        message: "Preencha pelo menos um campo para buscar.",
      });
      return;
    }

    clearErrors("form");
    setLoading(true);
    setErro("");
    setResultados([]);

    try {
      const response = await axiosInstance.post(QUERIES.LOCALIZE, data); // rota local
      setResultados(response.data || []);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      setErro(
        err.response?.data?.error || "Erro ao buscar dados. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mx-auto space-y-4">
        <header className="rounded-3xl border border-slate-200 bg-white/80 px-6 py-3 shadow-sm shadow-slate-200/70 backdrop-blur">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 md:text-2xl">
                Localizador de Pessoas
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 md:text-base">
                Pesquise com precisão usando dados básicos. Quanto mais
                informações, maior a assertividade na identificação.
              </p>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                <HiBadgeCheck className="text-lg" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Compliance
                </p>
                <p className="font-semibold text-slate-700">
                  Consulta segura e auditável
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr),minmax(0,1fr)]">
          <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm shadow-slate-200/60">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Dados de busca
                </p>
                <h2 className="mt-2 text-xl font-semibold text-slate-900">
                  Identificação do cidadão
                </h2>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                <HiSearch className="text-base" />
                Busca inteligente
              </span>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
              {(errors.form || erro) && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {errors.form?.message || erro}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Input
                  label="Nome completo"
                  name="Nome"
                  register={register}
                  icon={HiUser}
                  placeholder="Ex.: Mariana Souza"
                />
                <Input
                  label="CEP"
                  name="CEP"
                  register={register}
                  icon={HiLocationMarker}
                  placeholder="00000-000"
                />
                <Input
                  label="Número do endereço"
                  name="NumeroEnd"
                  register={register}
                  icon={HiIdentification}
                  placeholder="Ex.: 120"
                />
                <Input
                  label="Data de nascimento"
                  name="DataNascimento"
                  register={register}
                  icon={HiCalendar}
                  placeholder="ddmmaaaa"
                  maxLength={8}
                />
                <Input
                  label="Telefone"
                  name="Fone"
                  register={register}
                  icon={HiPhone}
                  placeholder="(00) 00000-0000"
                />
                <Input
                  label="E-mail"
                  name="email"
                  register={register}
                  type="email"
                  icon={HiMail}
                  placeholder="email@empresa.com"
                />
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  Informe ao menos um campo para executar a busca.
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-500/30 transition hover:bg-slate-800 disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <HiSearch className="text-base" />
                      Buscar pessoa
                    </>
                  )}
                </button>
              </div>
            </form>
          </section>
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm shadow-slate-200/60">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Resultados
              </p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                Pessoas localizadas
              </h2>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Total:{" "}
              <span className="font-semibold text-slate-900">
                {totalResultados}
              </span>
            </div>
          </div>

          {loading && (
            <div className="mt-6 flex items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-10 text-slate-600">
              <Spinner size="lg" />
              <span className="ml-3">Carregando resultados...</span>
            </div>
          )}

          {!loading && resultados.length > 0 && (
            <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200">
              <Table striped>
                <Table.Head>
                  <Table.HeadCell>Nome</Table.HeadCell>
                  <Table.HeadCell>CPF</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {resultados.map((pessoa, i) => {
                    const cpfValue = pessoa.cpf;
                    const isCopied = copiedCpf === cpfValue && cpfValue;
                    return (
                      <Table.Row key={i} className="bg-white">
                        <Table.Cell>{pessoa.nome || "-"}</Table.Cell>
                        <Table.Cell>
                          <div className="flex items-center justify-between gap-3">
                            <span>{formatDocument(cpfValue) || "-"}</span>
                            <button
                              type="button"
                              onClick={() => handleCopyCpf(cpfValue)}
                              disabled={!cpfValue}
                              className={[
                                "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition",
                                cpfValue
                                  ? "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                  : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed",
                              ].join(" ")}
                            >
                              {isCopied ? (
                                <>
                                  <HiCheck className="text-sm" />
                                  Copiado
                                </>
                              ) : (
                                <>
                                  <HiClipboardCopy className="text-sm" />
                                  Copiar
                                </>
                              )}
                            </button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          )}

          {!loading && resultados.length === 0 && buscaRealizada && (
            <div className="mt-6 flex h-48 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm font-semibold text-slate-500">
              Nenhum resultado encontrado para os dados informados.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Input = ({
  label,
  name,
  register,
  type = "text",
  maxLength,
  icon: Icon,
  placeholder,
}) => (
  <label className="flex flex-col gap-2 text-sm text-slate-600">
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
      {label}
    </span>
    <div className="relative">
      {Icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <Icon className="text-base" />
        </span>
      )}
      <input
        type={type}
        {...register(name)}
        maxLength={maxLength}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pl-10 text-sm text-slate-900 shadow-sm transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
      />
    </div>
  </label>
);

export default LocalizaPessoaPage;
